import { Dispatch } from 'react';

import { getAssetsInfo, getAssetsRequest } from 'api/assets';

import { ALL_ICE_ASSETS } from 'constants/assets';
import { TESTNET_ASSETS } from 'constants/assets-env';

import { getEnvClassicAssetData, getAssetFromString, getAssetString } from 'helpers/assets';
import { getIsTestnetEnv, getNetworkPassphrase } from 'helpers/env';
import { createAsset, createLumen } from 'helpers/token';

import { AssetInfo } from 'types/asset-info';
import { ClassicToken, Token, TokenType } from 'types/token';

import { getAssetMetadataCache, setAssetMetadataCache, persistAssetMetadata } from './cache';
import { ASSETS_ACTIONS, AssetSimple } from './types';

import { ActionResult, State } from '../types';

let cacheRevision = 0;
let publishTimer: ReturnType<typeof setTimeout>;

const publishMetadata = (dispatch: Dispatch<ActionResult>) => {
    clearTimeout(publishTimer);
    const network = getNetworkPassphrase();
    publishTimer = setTimeout(() => {
        if (network !== getNetworkPassphrase()) return;
        persistAssetMetadata();
        dispatch({
            type: ASSETS_ACTIONS.UPDATE_ASSET_INFO,
            payload: { assetInfo: getAssetMetadataCache() },
        });
    }, 0);
};

export function clearAssets() {
    cacheRevision += 1;
    clearTimeout(publishTimer);
    setAssetMetadataCache(new Map());
    persistAssetMetadata();
    return { type: ASSETS_ACTIONS.CLEAR_ASSETS };
}

export function getAssets() {
    return (dispatch: Dispatch<ActionResult>, state?: State): void => {
        if (state?.assetsStore.isLoading || state?.assetsStore.assets.length) return;
        dispatch({ type: ASSETS_ACTIONS.GET_ASSETS_START });

        const {
            asset: aquaStellarAsset,
            code: aquaCode,
            issuer: aquaIssuer,
        } = getEnvClassicAssetData('aqua');
        const {
            asset: usdcStellarAsset,
            code: usdcCode,
            issuer: usdcIssuer,
        } = getEnvClassicAssetData('usdc');

        getAssetsRequest()
            .then(assets => {
                dispatch({
                    type: ASSETS_ACTIONS.GET_ASSETS_SUCCESS,
                    payload: {
                        assets: [
                            aquaStellarAsset,
                            createLumen(),
                            usdcStellarAsset,
                            ...assets
                                .filter(
                                    asset =>
                                        !(
                                            (asset.code === aquaCode &&
                                                asset.issuer === aquaIssuer) ||
                                            (asset.code === usdcCode && asset.issuer === usdcIssuer)
                                        ),
                                )
                                .sort((a, b) => a.code.localeCompare(b.code)),
                        ],
                    },
                });
            })
            .catch(() => {
                dispatch({ type: ASSETS_ACTIONS.GET_ASSETS_FAIL });
            });
    };
}

export function processNewAssets(assets: Token[] | AssetSimple[]) {
    return (dispatch: Dispatch<ActionResult>): void => {
        const revision = cacheRevision;
        const network = getNetworkPassphrase();
        const cached = getAssetMetadataCache();

        const newAssets: ClassicToken[] | AssetSimple[] = assets.filter(
            asset =>
                (asset as Token).type !== TokenType.soroban &&
                !cached.has(`${asset.code}:${(asset as ClassicToken | AssetSimple).issuer}`) &&
                !createAsset(asset.code, (asset as ClassicToken | AssetSimple).issuer).isNative(),
        ) as ClassicToken[] | AssetSimple[];

        if (!cached.has(ALL_ICE_ASSETS[ALL_ICE_ASSETS.length - 1])) {
            newAssets.push(...ALL_ICE_ASSETS.map(str => getAssetFromString(str) as ClassicToken));
        }

        if (getIsTestnetEnv()) {
            const latest = new Map(cached);
            TESTNET_ASSETS.forEach(asset => {
                latest.set(`${asset.code}:${asset.issuer}`, asset as AssetInfo);
            });
            newAssets.forEach(asset => {
                const key = `${asset.code}:${asset.issuer}`;
                if (!latest.has(key))
                    latest.set(key, {
                        code: asset.code,
                        issuer: asset.issuer,
                        image: null,
                    } as AssetInfo);
            });
            if (latest.size === cached.size) return;
            setAssetMetadataCache(latest);
            publishMetadata(dispatch);
            return;
        }

        if (!newAssets.length) {
            return;
        }

        getAssetsInfo(newAssets)
            .then(res => {
                if (revision !== cacheRevision || network !== getNetworkPassphrase()) return;
                // Other requests may have completed since this batch started.
                // Merge into the latest cache instead of restoring the old snapshot.
                const current = getAssetMetadataCache();
                if (res.every(info => current.get(`${info.code}:${info.issuer}`) === info)) return;
                const latest = new Map(current);
                res.forEach(info => {
                    latest.set(getAssetString(createAsset(info.code, info.issuer)), info);
                });

                setAssetMetadataCache(latest);
                publishMetadata(dispatch);
            })
            .catch(() => {
                // Optional display metadata can be retried by the next consumer.
            });
    };
}
